import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Typography,
    Paper,
    CircularProgress,
} from '@mui/material';
import { LabelSheet } from './LabelSheet';
import { StartPositionSelector } from './StartPositionSelector';
import { LabelPrintJob } from '../types/LabelPrintJob';
import { usePrintLayout } from '../hooks/usePrintLayout';
import WordDocumentService from '../services/wordDocumentService';
import '../styles/print.css';

interface LabelPrintPreviewProps {
    /** The print job to preview */
    job: LabelPrintJob;
    /** Callback to go back to selection */
    onBack: () => void;
}

/**
 * LabelPrintPreview component - displays print preview and print button.
 *
 * Shows LabelSheet components for each A4 page in the job.
 * Provides print button that opens browser print dialog.
 * Allows user to select starting cell position for partially used sheets.
 */
export const LabelPrintPreview: React.FC<LabelPrintPreviewProps> = ({
    job,
    onBack,
}) => {
    const { t } = useTranslation();
    const [startPosition, setStartPosition] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const layout = usePrintLayout(job.items, startPosition);

    const handleDownloadWord = async () => {
        setIsGenerating(true);
        try {
            const wordService = new WordDocumentService();
            await wordService.generateDocument(job.items, startPosition);
        } catch (error) {
            console.error('Error generating Word document:', error);
            alert(t('labelPrinting.errorGeneratingDocument'));
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Box>
            {/* Control bar - will be hidden in print */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }} className="no-print">
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('labelPrinting.printPreview')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t('labelPrinting.totalLabels', {
                                count: layout.totalLabels,
                            })}{' '}
                            •{' '}
                            {t('labelPrinting.totalSheets', {
                                count: layout.totalSheets,
                            })}
                        </Typography>
                        {layout.unusedLabelSlots > 0 && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                mt={1}
                            >
                                {t('labelPrinting.unusedSlots', {
                                    count: layout.unusedLabelSlots,
                                })}
                            </Typography>
                        )}
                        <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                                display: 'block',
                                mt: 1,
                                fontWeight: 'medium',
                            }}
                        >
                            {t('labelPrinting.printInstructions')}
                        </Typography>
                        {startPosition > 0 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    fontStyle: 'italic',
                                }}
                            >
                                Startposition: Cell {startPosition + 1} (Rad{' '}
                                {Math.floor(startPosition / 3) + 1}, Kolumn{' '}
                                {(startPosition % 3) + 1})
                            </Typography>
                        )}
                    </Box>
                    <Box display="flex" gap={2} alignItems="center">
                        <StartPositionSelector
                            currentPosition={startPosition}
                            onPositionChange={setStartPosition}
                        />
                        <Button variant="outlined" onClick={onBack}>
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDownloadWord}
                            disabled={isGenerating}
                            startIcon={
                                isGenerating ? (
                                    <CircularProgress size={20} />
                                ) : null
                            }
                        >
                            {isGenerating
                                ? t('labelPrinting.generating')
                                : t('labelPrinting.downloadWord')}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Print preview - label sheets */}
            <div className="label-sheet-container">
                {layout.sheets.map((sheet) => (
                    <LabelSheet
                        key={`sheet-${sheet.sheetNumber}`}
                        sheet={sheet}
                    />
                ))}
            </div>
        </Box>
    );
};
